import type {DiscountBand} from './discountTable';

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export type ProductDiscount = {
  name: string;
  discount: number;
  max: number;
  addon?: number;
  isCapped: boolean;
  baseComponents: {
    base: number;
    paymentTerm: number;
    newDeal: number;
    enterprise: number;
    monthlyMin: number;
  };
};

export type CalculationResult =
  | {type: 'error'; message: string}
  | {
      type: 'success';
      tcv: number;
      acv: number;
      band: DiscountBand;
      productDiscounts: ProductDiscount[];
      warnings: string[];
    };

export type DealFormValues = {
  tcv: string;
  years: string;
  dealType: string;
  paymentFreq: string;
  tier: string;
  monthlyMin: string;
  products: {
    serverless: boolean;
    assistant: boolean;
    byoc: boolean;
  };
};

export function calculateDiscount(
  values: DealFormValues,
  findBand: (acv: number) => DiscountBand | null,
): CalculationResult {
  const tcv = parseFloat(values.tcv);
  const years = parseInt(values.years, 10);
  const {dealType, paymentFreq, tier} = values;
  const monthlyMin = parseFloat(values.monthlyMin);
  const {products} = values;

  if (!tcv || !years || !dealType || !paymentFreq || !tier) {
    return {type: 'error', message: 'Please fill in all required fields.'};
  }

  if ((paymentFreq === 'upfront2' && years !== 2) || (paymentFreq === 'upfront3' && years !== 3)) {
    return {
      type: 'error',
      message: `Payment frequency "${paymentFreq}" is not valid for a ${years}-year contract.`,
    };
  }

  if (!products.serverless && !products.assistant && !products.byoc) {
    return {type: 'error', message: 'Please select at least one product.'};
  }

  const acv = tcv / years;

  if (acv >= 500000) {
    return {
      type: 'error',
      message:
        'High-value deal: for deals with ACV of $500,000 or more, please reach out to Finance for approval.',
    };
  }

  const band = findBand(acv);
  if (!band) {
    return {
      type: 'error',
      message: 'ACV amount is outside the standard discount bands. Please contact Finance.',
    };
  }

  const baseDiscount = band.rep;
  let paymentTermDiscount = 0;
  let newDealDiscount = 0;
  let enterpriseDiscount = 0;
  let monthlyMinDiscount = 0;

  if (years === 1) {
    if (paymentFreq === 'quarterly') {
      paymentTermDiscount = band.quarterly;
    } else if (paymentFreq === 'semi') {
      paymentTermDiscount = band.semi;
    }
  } else if (years === 2) {
    if (paymentFreq === 'upfront2') {
      paymentTermDiscount = band.twoyd_upfront;
    } else if (paymentFreq === 'annual') {
      paymentTermDiscount = band.twoyd_annual;
    } else if (paymentFreq === 'quarterly') {
      paymentTermDiscount = band.twoyd_annual + band.quarterly;
    } else if (paymentFreq === 'semi') {
      paymentTermDiscount = band.twoyd_annual + band.semi;
    }
  } else if (years === 3) {
    if (paymentFreq === 'upfront3') {
      paymentTermDiscount = band.threeyd_upfront;
    } else if (paymentFreq === 'annual') {
      paymentTermDiscount = band.threeyd_annual;
    } else if (paymentFreq === 'quarterly') {
      paymentTermDiscount = band.threeyd_annual + band.quarterly;
    } else if (paymentFreq === 'semi') {
      paymentTermDiscount = band.threeyd_annual + band.semi;
    }
  }

  if (dealType === 'new') {
    newDealDiscount = band.firstYear;
  }

  if (tier === 'enterprise') {
    enterpriseDiscount = band.enterprise;
  }

  if (monthlyMin >= 80) {
    monthlyMinDiscount = band.monthlyMin;
  }

  const totalBaseDiscount =
    baseDiscount + paymentTermDiscount + newDealDiscount + enterpriseDiscount + monthlyMinDiscount;

  const productDiscounts: ProductDiscount[] = [];
  const baseComponents = {
    base: baseDiscount,
    paymentTerm: paymentTermDiscount,
    newDeal: newDealDiscount,
    enterprise: enterpriseDiscount,
    monthlyMin: monthlyMinDiscount,
  };

  if (products.serverless) {
    const calculated = totalBaseDiscount;
    productDiscounts.push({
      name: 'Serverless (Database)',
      discount: Math.min(calculated, band.db_max),
      max: band.db_max,
      isCapped: calculated > band.db_max,
      baseComponents,
    });
  }

  if (products.assistant) {
    const calculated = totalBaseDiscount + band.assistant;
    productDiscounts.push({
      name: 'Assistant',
      discount: Math.min(calculated, band.asst_max),
      max: band.asst_max,
      addon: band.assistant,
      isCapped: calculated > band.asst_max,
      baseComponents,
    });
  }

  if (products.byoc) {
    const calculated = totalBaseDiscount + band.byoc;
    productDiscounts.push({
      name: 'BYOC',
      discount: Math.min(calculated, band.byoc_max),
      max: band.byoc_max,
      addon: band.byoc,
      isCapped: calculated > band.byoc_max,
      baseComponents,
    });
  }

  const warnings: string[] = [];
  if (paymentFreq === 'quarterly') {
    warnings.push(
      'Quarterly payment terms include a 5% discount penalty and require Finance approval.',
    );
  } else if (paymentFreq === 'semi') {
    warnings.push(
      'Semi-annual payment terms include a 3% discount penalty and require Finance approval.',
    );
  }
  if (!isNaN(monthlyMin) && monthlyMin < 80) {
    warnings.push('Monthly minimum is below 80% threshold. No additional discount applied.');
  }

  return {type: 'success', tcv, acv, band, productDiscounts, warnings};
}
